import { parseDate } from '@ark-ui/solid'
import dayjs from 'dayjs'
import { Calendar, X } from 'lucide-solid'
import { type JSX, createMemo, createSignal } from 'solid-js'
import { Index, Portal } from 'solid-js/web'
import * as DatePicker from 'src/components/ui/date-picker'
import { IconButton } from 'src/components/ui/icon-button'
import { input } from 'styled-system/recipes'

export const ControlledDatePicker = (props: { label: string | JSX.Element, value: any, setValue: (value: any) => void }) => {
  const value = createMemo(() => {
    // 1. Guard Clause: Check for undefined, null, or empty string
    if (props.value === undefined || props.value === null || props.value === '') {
      return undefined;
    }

    // 3. Handle standard Date strings or Date objects
    return [parseDate(dayjs(props.value).format('YYYY-MM-DD'))];
  });
  const [open, setOpen] = createSignal(false)

  return (
    <DatePicker.Root value={value()}
      onValueChange={(e) => {
        const selectedDate = e.value[0]

        // Use .format() to explicitly construct the string without milliseconds and with +00:00
        const stringValue = selectedDate
          ? dayjs.utc(selectedDate.toString()).format('YYYY-MM-DDTHH:mm:ss+00:00')
          : undefined

        props.setValue(stringValue)
      }}
      open={open()} onOpenChange={() => setOpen(!open())}>
      <DatePicker.Label>{props.label}</DatePicker.Label>

      <DatePicker.Control>
        <DatePicker.Input class={input({
          size: 'md',
        })} />
        <DatePicker.Trigger asChild={props => <IconButton size='xs' {...props}><Calendar /></IconButton>} />
        <DatePicker.ClearTrigger asChild={props => <IconButton size='xs' {...props}><X /></IconButton>} />
      </DatePicker.Control>

      <Portal>
        <DatePicker.Positioner zIndex={open() ? 'modal!' : 'auto'}>
          <DatePicker.Content>
            <DatePicker.YearSelect />
            <DatePicker.MonthSelect />
            <DatePicker.View view="day">
              <DatePicker.Context>
                {(context) => (
                  <>
                    <DatePicker.ViewControl>
                      <DatePicker.PrevTrigger>Prev</DatePicker.PrevTrigger>
                      <DatePicker.ViewTrigger>
                        <DatePicker.RangeText />
                      </DatePicker.ViewTrigger>
                      <DatePicker.NextTrigger>Next</DatePicker.NextTrigger>
                    </DatePicker.ViewControl>

                    <DatePicker.Table>
                      <DatePicker.TableHead>
                        <DatePicker.TableRow>
                          <Index each={context().weekDays}>
                            {(weekDay) => <DatePicker.TableHeader>{weekDay().short}</DatePicker.TableHeader>}
                          </Index>
                        </DatePicker.TableRow>
                      </DatePicker.TableHead>

                      <DatePicker.TableBody>
                        <Index each={context().weeks}>
                          {(week) => (
                            <DatePicker.TableRow>
                              <Index each={week()}>
                                {(day) => (
                                  <DatePicker.TableCell value={day()}>
                                    <DatePicker.TableCellTrigger>{day().day}</DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                )}
                              </Index>
                            </DatePicker.TableRow>
                          )}
                        </Index>
                      </DatePicker.TableBody>
                    </DatePicker.Table>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>

            <DatePicker.View view="month">
              <DatePicker.Context>
                {(context) => (
                  <>
                    <DatePicker.ViewControl>
                      <DatePicker.PrevTrigger>Prev</DatePicker.PrevTrigger>
                      <DatePicker.ViewTrigger>
                        <DatePicker.RangeText />
                      </DatePicker.ViewTrigger>
                      <DatePicker.NextTrigger>Next</DatePicker.NextTrigger>
                    </DatePicker.ViewControl>

                    <DatePicker.Table>
                      <DatePicker.TableBody>
                        <Index each={context().getMonthsGrid({ columns: 4, format: 'short' })}>
                          {(months) => (
                            <DatePicker.TableRow>
                              <Index each={months()}>
                                {(month) => (
                                  <DatePicker.TableCell value={month().value}>
                                    <DatePicker.TableCellTrigger>{month().label}</DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                )}
                              </Index>
                            </DatePicker.TableRow>
                          )}
                        </Index>
                      </DatePicker.TableBody>
                    </DatePicker.Table>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>

            <DatePicker.View view="year">
              <DatePicker.Context>
                {(context) => (
                  <>
                    <DatePicker.ViewControl>
                      <DatePicker.PrevTrigger>Prev</DatePicker.PrevTrigger>
                      <DatePicker.ViewTrigger>
                        <DatePicker.RangeText />
                      </DatePicker.ViewTrigger>
                      <DatePicker.NextTrigger>Next</DatePicker.NextTrigger>
                    </DatePicker.ViewControl>

                    <DatePicker.Table>
                      <DatePicker.TableBody>
                        <Index each={context().getYearsGrid({ columns: 4 })}>
                          {(years) => (
                            <DatePicker.TableRow>
                              <Index each={years()}>
                                {(year) => (
                                  <DatePicker.TableCell value={year().value}>
                                    <DatePicker.TableCellTrigger>{year().label}</DatePicker.TableCellTrigger>
                                  </DatePicker.TableCell>
                                )}
                              </Index>
                            </DatePicker.TableRow>
                          )}
                        </Index>
                      </DatePicker.TableBody>
                    </DatePicker.Table>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>
          </DatePicker.Content>
        </DatePicker.Positioner>
      </Portal>
    </DatePicker.Root>
  )
}
